import { Components, CSSObject, Theme } from '@mui/material'
import { DISABLED_OPACITY } from './disabledOpacity'
import {
  IconButtonColour,
  iconButtonColourStyles,
  iconButtonIconStyle,
  iconButtonSizeStyles,
  IconButtonVariant,
} from './iconButtonStyles'

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    // IconButton doesn't have variants in MUI, so adding them is different
    variant?: IconButtonVariant
  }
}
const loadingIndicatorSelector = '& .MuiIconButton-loadingIndicator'

export const secondaryGhostIconButtonStyle = (theme: Theme): CSSObject => {
  const { base, hover, disabled } = iconButtonColourStyles(theme).ghost.secondary
  return {
    ...base,
    '&:hover': hover,
    '&.Mui-disabled': { ...disabled, opacity: DISABLED_OPACITY },
  }
}

const colourStyle = (theme: Theme, variant: IconButtonVariant, colour: IconButtonColour): CSSObject => {
  const { base, hover, disabled } = iconButtonColourStyles(theme)[variant][colour]
  return {
    ...base,
    '&:hover': hover,
    [`&.Mui-disabled, ${loadingIndicatorSelector}`]: disabled,
  }
}

export const MuiIconButton: Components<Omit<Theme, 'components'>>['MuiIconButton'] = {
  defaultProps: {
    disableRipple: true,
    variant: 'ghost',
    color: 'secondary',
  },
  styleOverrides: {
    root: {
      '&.Mui-disabled': {
        opacity: DISABLED_OPACITY,
      },
      // Hide whatever the button renders while loading, not just SvgIcons
      '&.MuiIconButton-loading > *:not(.MuiIconButton-loadingWrapper)': {
        opacity: 0,
      },
      ...iconButtonIconStyle,
    },
  },
  variants: [
    {
      props: { variant: 'contained' },
      style: {},
    },
    {
      props: { variant: 'ghost' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.invisible,
      }),
    },
    // Colour
    {
      props: { color: 'primary', variant: 'contained' },
      style: ({ theme }) => colourStyle(theme, 'contained', 'primary'),
    },
    {
      props: { color: 'primary', variant: 'ghost' },
      style: ({ theme }) => colourStyle(theme, 'ghost', 'primary'),
    },
    {
      props: { color: 'secondary', variant: 'contained' },
      style: ({ theme }) => colourStyle(theme, 'contained', 'secondary'),
    },
    {
      props: { color: 'secondary', variant: 'ghost' },
      style: ({ theme }) => colourStyle(theme, 'ghost', 'secondary'),
    },
    {
      props: { color: 'error', variant: 'contained' },
      style: ({ theme }) => colourStyle(theme, 'contained', 'error'),
    },
    {
      props: { color: 'error', variant: 'ghost' },
      style: ({ theme }) => colourStyle(theme, 'ghost', 'error'),
    },
    // Size
    {
      props: { size: 'large' },
      style: ({ theme }) => iconButtonSizeStyles(theme).large,
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => iconButtonSizeStyles(theme).medium,
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => iconButtonSizeStyles(theme).small,
    },
  ],
}
